import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CardDecomposition, CardIssue, DecompositionLever, DecompositionWindow } from '../types';
import { currencyPrefix, WEEKS_PER_MONTH } from '../logic';
import { dateLabel } from './TrendCharts';
import { formatImpact } from './CardFaceSlots';

const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const BORDER = '#E2E8F0';
const GRID = '#F1F5F9';
const PAPER = '#FFFFFF';
const SERIES = '#2563EB';

// The chart is drawn at its natural size and scaled by the viewBox, so these numbers are also
// the CSS pixels it occupies at full size. 520 is what the detail column actually offers (~514
// measured), and the svg is capped there rather than stretched: a wider viewport gets whitespace
// beside the chart instead of oversized type. Two label lines fit between PLOT.bottom and CHART_H.
const CHART_W = 520;
const CHART_H = 220;
const PLOT = { top: 18, bottom: 176, left: 40, right: 14 };

function isNumber(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value);
}

// Whole dollars above $10, cents below it. A blanket whole-dollar rule reads well on order
// value and destroys cost per click, where $1.42 and $1.09 are a 30% difference that both
// round to $1.
function formatLeverValue(value: number | null, unit: DecompositionLever['unit'], store: string | null): string {
  if (!isNumber(value)) return '-';
  if (unit === 'usd') return Math.abs(value) >= 10 ? `${currencyPrefix(store)}${Math.round(value)}` : `${currencyPrefix(store)}${value.toFixed(2)}`;
  if (unit === 'rate') return `${(value * 100).toFixed(1)}%`;
  return value.toFixed(2);
}

/** Which way the lever's own measure moved. Not whether that helped: see the return column. */
function formatMove(lever: DecompositionLever): string {
  if (!lever.measured) return 'not measured';
  if (!isNumber(lever.deltaPct)) return '-';
  const pct = Math.abs(lever.deltaPct * 100);
  if (pct < 0.05) return 'flat';
  return `${lever.deltaPct > 0 ? 'up' : 'down'} ${pct.toFixed(pct < 10 ? 1 : 0)}%`;
}

/**
 * The lever's return contribution priced at the campaign's current spend rate. Signed, because
 * a lever that gave return back is a different fact from one that took it away, and formatImpact
 * prints magnitude only.
 */
function formatDollarEffect(contribution: number | null, spend7d: number | null, store: string | null): string {
  if (!isNumber(contribution) || !isNumber(spend7d) || spend7d === 0) return '-';
  const monthly = contribution * spend7d * WEEKS_PER_MONTH;
  return `${monthly > 0 ? '+' : '-'}${formatImpact(monthly, currencyPrefix(store))}`;
}

function formatReturn(value: number | null): string {
  return isNumber(value) ? `${value.toFixed(2)}x` : '-';
}

function formatContribution(value: number | null): string {
  return isNumber(value) ? `${value > 0 ? '+' : ''}${value.toFixed(2)}x` : '-';
}

function formatPercent(value: number | null): string {
  return isNumber(value) ? `${(value * 100).toFixed(1)}%` : '-';
}

function formatPp(value: number): string {
  return `${value > 0 ? '+' : ''}${value.toFixed(1)} pp`;
}

// There was a "Baseline window: healthy, cleared target, rejected 12 blocks." line here. Every
// term in it named an internal of the baseline picker: `kind`, the bar it had to clear, the count
// of candidate windows it threw out. None of that is answerable by the reader and none of it
// changes what they do (L, 2026-08-04). The dates the comparison covers, which is the part that
// does, are in the section caption.

// SVG text does not wrap, and a lever label runs wider than the slot one bar gets, so it is
// broken across lines that fit. A character is about 5 units wide at AXIS_FONT, and slot is the
// per-bar spacing, which shrinks as levers are added. Three lines is the worst case the current
// vocabulary produces ("Clicks becoming sessions" at eight bars) and CHART_H allows for it.
const AXIS_FONT = 10;
const AXIS_LINE = 11;

function wrapLabel(label: string, slot: number): string[] {
  const maxChars = Math.max(6, Math.floor(slot / 5));
  const lines: string[] = [];
  label.split(' ').forEach(word => {
    const last = lines.length > 0 ? lines[lines.length - 1] : null;
    if (last != null && `${last} ${word}`.length <= maxChars) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  });
  return lines;
}

// The promo figures that used to sit here (average discount, discounted order share, units per
// order) were store-daily: every order the store took in the window, on any campaign, from any
// source. On a card about one campaign they answered a question nobody asked and invited the
// reader to treat a store-wide promo week as this campaign's explanation. The warehouse cannot
// attribute an order's discount to a campaign, so there is no campaign-grain version to show
// instead, and the honest move is to show nothing (L, 2026-08-03).
//
// The window's own provenance is a different fact and it survives, in the section caption: which
// dates each side of the comparison covers, and how the baseline was chosen.
function windowSpan(window: DecompositionWindow): string {
  return `${dateLabel(window.from)} to ${dateLabel(window.to)}`;
}

function AuctionTable({ decomposition }: { decomposition: CardDecomposition }) {
  const auction = decomposition.auctionPosition;
  if (!auction) return null;
  return (
    <Box sx={{ mt: 2.5 }}>
      <Box component="table" sx={{ width: '100%', maxWidth: 540, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
        <Box component="caption" sx={{ textAlign: 'left', captionSide: 'top', color: MUTED, fontSize: 12, pb: 0.75 }}>Auction position, {auction.cohort}</Box>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ textAlign: 'left', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Measure</Box>
            <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Baseline</Box>
            <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Current</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {[
            { label: 'Search impression share', pair: auction.searchIs },
            { label: 'Top', pair: auction.topIs },
            { label: 'Absolute top', pair: auction.absTopIs },
          ].map(row => (
            <Box component="tr" key={row.label}>
              <Box component="td" sx={{ py: 0.5, fontSize: 12 }}>{row.label}</Box>
              <Box component="td" sx={{ py: 0.5, textAlign: 'right', fontSize: 12 }}>{formatPercent(row.pair.baseline)}</Box>
              <Box component="td" sx={{ py: 0.5, textAlign: 'right', fontSize: 12 }}>{formatPercent(row.pair.current)}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * How the add-to-cart lever splits, as rows. This was a sentence carrying three labeled numbers
 * with their definitions in parentheses, which is a table typed out longhand (L, 2026-08-04). The
 * parenthetical definitions become the rows' second column, so the label and its meaning sit
 * beside the figure instead of interrupting it.
 */
function AtcSplitTable({ decomposition }: { decomposition: CardDecomposition }) {
  const split = decomposition.atcSplit;
  if (!split) return null;
  const rows = [
    { label: 'Rate effect', meaning: 'same pages converting differently', value: formatPp(split.rateEffectPp) },
    { label: 'Mix effect', meaning: 'traffic shifting between pages', value: formatPp(split.mixEffectPp) },
    { label: 'Coverage', meaning: 'add-to-cart sessions on URLs in both windows', value: formatPercent(split.coverage) },
  ];
  return (
    <Box sx={{ mt: 2.5 }}>
      <Box component="table" sx={{ width: '100%', maxWidth: 540, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
        <Box component="caption" sx={{ textAlign: 'left', captionSide: 'top', color: MUTED, fontSize: 12, pb: 0.75 }}>Add-to-cart effects</Box>
        <Box component="tbody">
          {rows.map(row => (
            <Box component="tr" key={row.label}>
              <Box component="td" sx={{ py: 0.5, fontSize: 12, whiteSpace: 'nowrap' }}>{row.label}</Box>
              <Box component="td" sx={{ py: 0.5, pl: 2, fontSize: 12, color: MUTED }}>{row.meaning}</Box>
              <Box component="td" sx={{ py: 0.5, pl: 2, textAlign: 'right', fontSize: 12 }}>{row.value}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The five levers as a table, ordered by how much of the return move each one carries. This is
 * the primary answer to "why did return move" and it sits in front of the disclosure, not behind
 * it: the chart says the same thing in bars, but a row of bars is something to interpret and a
 * sorted table is something to read.
 *
 * A finding that measures a lever's own quantity prints under that lever's row, so the reader
 * does not have to pair a list of problems with a list of numbers by eye. A measured lever with
 * no such finding says so rather than leaving the silence to be read as "nothing to explain".
 */
function LeverTable({ decomposition, issues, spend7d, store }: { decomposition: CardDecomposition; issues: CardIssue[]; spend7d: number | null; store: string | null }) {
  const rows = useMemo(() => {
    const weight = (lever: DecompositionLever) => (lever.measured && isNumber(lever.roasContribution) ? Math.abs(lever.roasContribution) : -1);
    return decomposition.levers.slice().sort((first, second) => weight(second) - weight(first));
  }, [decomposition.levers]);
  const explanations = useMemo(() => {
    const map = new Map<string, CardIssue[]>();
    issues.forEach(issue => {
      if (!issue.explainsLever) return;
      map.set(issue.explainsLever, [...(map.get(issue.explainsLever) ?? []), issue]);
    });
    return map;
  }, [issues]);
  // Only a couple of checks measure a lever's own quantity, so on most cards no row has an
  // explanation. Repeating "no check explains this one" under all five reads as a broken table,
  // and it is the same sentence five times. Say it once, under the table, and let the rows be
  // numbers. Where some rows ARE explained, the per-row absence is real information and stays.
  const anyExplained = explanations.size > 0;

  return (
    <>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums', mb: anyExplained ? 1.5 : 0.5 }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ textAlign: 'left', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Lever</Box>
          <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Before</Box>
          <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>After</Box>
          <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Change</Box>
          <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Effect on return</Box>
          <Box component="th" sx={{ textAlign: 'right', color: MUTED, fontSize: 11, fontWeight: 600, pb: 0.5 }}>Per month</Box>
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map(lever => {
          const explaining = explanations.get(lever.key) ?? [];
          return (
            <React.Fragment key={lever.key}>
              <Box component="tr" sx={{ borderTop: `1px solid ${GRID}` }}>
                <Box component="td" sx={{ py: 0.75, fontSize: 12.5, fontWeight: 600 }}>{lever.label}</Box>
                <Box component="td" sx={{ py: 0.75, pl: 1.5, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap', color: MUTED }}>{lever.measured ? formatLeverValue(lever.baseline, lever.unit, store) : '-'}</Box>
                <Box component="td" sx={{ py: 0.75, pl: 1.5, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap' }}>{lever.measured ? formatLeverValue(lever.current, lever.unit, store) : '-'}</Box>
                <Box component="td" sx={{ py: 0.75, pl: 1.5, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap', color: lever.measured ? INK : MUTED }}>{formatMove(lever)}</Box>
                <Box component="td" sx={{ py: 0.75, pl: 1.5, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap' }}>{lever.measured ? formatContribution(lever.roasContribution) : '-'}</Box>
                <Box component="td" sx={{ py: 0.75, pl: 1.5, textAlign: 'right', fontSize: 12.5, whiteSpace: 'nowrap' }}>{lever.measured ? formatDollarEffect(lever.roasContribution, spend7d, store) : '-'}</Box>
              </Box>
              {(explaining.length > 0 || !lever.measured || anyExplained) && (
                <Box component="tr">
                  <Box component="td" colSpan={6} sx={{ pb: 0.75, pl: 0 }}>
                    {explaining.length > 0 ? explaining.map(issue => (
                      <Typography key={issue.key} variant="caption" sx={{ display: 'block', color: INK, maxWidth: '72ch' }}>
                        {issue.title}: {issue.whatWeSee}
                      </Typography>
                    )) : lever.measured ? (
                      <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>No check explains this one.</Typography>
                    ) : (
                      <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>{lever.unmeasuredReason ?? 'No reason given.'}</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
    {!anyExplained && (
      <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: MUTED }}>
        None of these moves is explained by a check. The checks below name what went wrong; only a few of them measure one of these five quantities directly, so the two lists do not have to line up.
      </Typography>
    )}
    </>
  );
}

function WaterfallChart({ decomposition, store }: { decomposition: CardDecomposition; store: string | null }) {
  const measured = useMemo(() => decomposition.levers
    .filter(lever => lever.measured && isNumber(lever.roasContribution))
    .sort((first, second) => Math.abs(second.roasContribution as number) - Math.abs(first.roasContribution as number)), [decomposition.levers]);
  const baseline = decomposition.roasBaseline;
  const current = decomposition.roasCurrent;
  let running = baseline;
  const bars: { label: string; start: number; end: number; contribution: number | null; lever?: DecompositionLever }[] = [];
  if (isNumber(baseline)) bars.push({ label: 'Baseline', start: 0, end: baseline, contribution: null });
  measured.forEach(lever => {
    if (!isNumber(running) || !isNumber(lever.roasContribution)) return;
    const start = running;
    running += lever.roasContribution;
    bars.push({ label: lever.label, start, end: running, contribution: lever.roasContribution, lever });
  });
  if (isNumber(current)) bars.push({ label: 'Current', start: 0, end: current, contribution: null });

  const values = bars.flatMap(bar => [bar.start, bar.end]);
  const low = Math.min(0, ...values);
  const high = Math.max(0, ...values);
  const pad = (high - low || 1) * 0.12;
  const min = low - pad;
  const max = high + pad;
  const y = (value: number) => PLOT.bottom - ((value - min) / (max - min)) * (PLOT.bottom - PLOT.top);
  const zeroY = y(0);
  const step = bars.length > 0 ? (CHART_W - PLOT.left - PLOT.right) / bars.length : 0;
  const barWidth = Math.min(74, Math.max(30, step - 14));

  return (
    <Box>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label="Return contribution waterfall" style={{ display: 'block', width: '100%', maxWidth: CHART_W, height: 'auto' }}>
        {[min, (min + max) / 2, max].map(value => (
          <g key={value}>
            <line x1={PLOT.left} x2={CHART_W - PLOT.right} y1={y(value)} y2={y(value)} stroke={value === 0 ? FAINT : GRID} strokeWidth="1" />
            <text x={PLOT.left - 8} y={y(value) + 3.5} fontSize="10.5" fill={FAINT} textAnchor="end" style={{ fontVariantNumeric: 'tabular-nums' }}>{value.toFixed(1)}x</text>
          </g>
        ))}
        {bars.map((bar, index) => {
          const x = PLOT.left + step * index + (step - barWidth) / 2;
          const top = y(Math.max(bar.start, bar.end));
          const height = Math.max(1, Math.abs(y(bar.start) - y(bar.end)));
          const details = bar.lever ? `Current ${formatLeverValue(bar.lever.current, bar.lever.unit, store)}. Baseline ${formatLeverValue(bar.lever.baseline, bar.lever.unit, store)}.` : '';
          return (
            <g key={`${bar.label}-${index}`}>
              {index < bars.length - 1 && <line x1={x + barWidth} x2={x + step} y1={y(bar.end)} y2={y(bar.end)} stroke={FAINT} strokeWidth="1" strokeDasharray="2 2" />}
              <rect x={x} y={top} width={barWidth} height={height} rx="3" fill={bar.contribution == null ? INK : SERIES}>
                <title>{bar.label}{bar.contribution != null ? `, contribution ${formatContribution(bar.contribution)}. ` : '. '}{details}</title>
              </rect>
              <text x={x + barWidth / 2} y={Math.max(PLOT.top - 3, top - 5)} fontSize="10.5" fill={INK} textAnchor="middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {bar.contribution != null ? formatContribution(bar.contribution) : formatReturn(bar.end)}
              </text>
              <text x={x + barWidth / 2} y={PLOT.bottom + 16} fontSize={AXIS_FONT} fill={MUTED} textAnchor="middle">
                {wrapLabel(bar.label, step).map((line, lineIndex) => (
                  <tspan key={line} x={x + barWidth / 2} dy={lineIndex === 0 ? 0 : AXIS_LINE}>{line}</tspan>
                ))}
              </text>
            </g>
          );
        })}
        <text x={PLOT.left} y={zeroY} dx="-8" dy="3.5" fontSize="10.5" fill={FAINT} textAnchor="end" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {zeroY < PLOT.top || zeroY > PLOT.bottom ? '' : '0.0x'}
        </text>
      </svg>
      <Typography variant="caption" sx={{ display: 'block', color: MUTED, mt: 0.5 }}>Bars show signed return units. Hover a bar for its current and baseline values.</Typography>
    </Box>
  );
}

export default function Waterfall({ decomposition, id, issues = [], spend7d = null, store }: { decomposition?: CardDecomposition; id?: string; issues?: CardIssue[]; spend7d?: number | null; store: string | null }) {
  const [open, setOpen] = React.useState(false);
  // The hash is honored once. DetailView refetches the item after claim, resolve, dismiss and
  // reopen, and each refetch hands down a new decomposition object, so an effect keyed on that
  // alone would re-expand this panel and scroll the page back to it under an operator who had
  // deliberately collapsed it and moved on.
  const hashHonored = React.useRef(false);

  useEffect(() => {
    if (hashHonored.current || !decomposition || !id || window.location.hash !== `#${id}`) return;
    hashHonored.current = true;
    setOpen(true);
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
  }, [decomposition, id]);

  if (!decomposition) return null;
  const unmeasured = decomposition.levers.filter(lever => !lever.measured);

  return (
    <Box id={id} component="section" sx={{ mb: 3, scrollMarginTop: 16 }} aria-labelledby={id ? `${id}-heading` : undefined}>
      <Typography id={id ? `${id}-heading` : undefined} component="h2" sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
        Why return moved
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: MUTED }}>
        Return went from {formatReturn(decomposition.roasBaseline)} to {formatReturn(decomposition.roasCurrent)}, comparing {windowSpan(decomposition.baselineWindow)} against {windowSpan(decomposition.currentWindow)}.
      </Typography>
      {/* One disclosure, closed. The chart came up first when this opened, and the whole section
          opened on arrival, so the reader met the arithmetic of the move before the problems that
          caused it. The move belongs in the summary line above; everything that breaks it into
          parts is for the reader who has already decided to go looking (L, 2026-08-03). */}
      <Typography component="button" type="button" aria-expanded={open} aria-controls={id ? `${id}-content` : undefined} onClick={() => setOpen(value => !value)} sx={{ display: 'block', border: 0, bgcolor: 'transparent', color: '#6366F1', cursor: 'pointer', p: '4px 0 10px', fontSize: 12.5, fontWeight: 600, textAlign: 'left', '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: 2 } }}>
        {open ? 'Hide the breakdown' : 'Break the move into its five levers'}
      </Typography>
      {open && (
        <Box id={id ? `${id}-content` : undefined} sx={{ p: '18px 20px 20px', border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: PAPER, overflow: 'hidden' }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: MUTED }}>
            {decomposition.revenueBasis === 'triple_whale' ? 'Return measured against Triple Whale revenue.' : 'Return measured against Google revenue.'}
            {' '}Per-month figures price each lever's effect at the campaign's current spend.
          </Typography>
          <LeverTable decomposition={decomposition} issues={issues} spend7d={spend7d} store={store} />
          {!decomposition.contributionsComplete && (
            <Typography variant="body2" sx={{ mb: 1.5, color: INK }}>
              {unmeasured.length === 1 ? 'One lever dropped out.' : `${unmeasured.length} levers dropped out.`} These rows do not add up to the whole move.
            </Typography>
          )}
          <WaterfallChart decomposition={decomposition} store={store} />
          <AtcSplitTable decomposition={decomposition} />
          <AuctionTable decomposition={decomposition} />
        </Box>
      )}
    </Box>
  );
}
